import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import ProductCatalogNav from '@/components/products/ProductCatalogNav'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import { productQueries } from '@/lib/services/products/products.query'
import { createFileRoute, Link, notFound, stripSearchParams } from '@tanstack/react-router'
import { ArrowRight, Boxes, Flower2, LibraryBig, Printer, Tags } from 'lucide-react'
import {
  productListSearchSchema,
  strippedProductListSearchParams,
  getProductListFilters,
  ProductListTable,
} from './all-products'

export function hasProductCatalogAccess(ability) {
  return (
    ability.can('list', 'products') ||
    ability.can('list', 'collections') ||
    ability.can('edit', 'products')
  )
}

export function buildProductCatalogSections({
  orgId,
  path,
  canListProducts,
  canViewCollections,
  canViewPriceChanges,
}) {
  return [
    {
      id: 'inventory',
      title: 'Inventory',
      description: "Track what's on hand and update stock counts.",
      icon: Boxes,
      link: path.inventory.index().using({ orgId }),
      show: canListProducts,
    },
    {
      id: 'collections',
      title: 'Collections',
      description: 'Group products for holidays, promotions, and storefront merchandising.',
      icon: LibraryBig,
      link: path.collections.index().using({ orgId }),
      show: canViewCollections,
    },
    {
      id: 'price-changes',
      title: 'Price Changes',
      description: 'Manage active and scheduled pricing adjustments across the catalog.',
      icon: Tags,
      link: path.priceChanges.index().using({ orgId }),
      show: canViewPriceChanges,
    },
  ].filter((section) => section.show)
}

function ProductCatalogSectionCard({ section, compact = false }) {
  const Icon = section.icon

  return (
    <Link
      {...section.link.link}
      aria-label={`${section.title}: ${section.description}`}
      className={
        compact
          ? 'group flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm'
          : 'group flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm'
      }
    >
      <span
        className={
          compact
            ? 'inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-interaction'
            : 'mt-0.5 inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-interaction'
        }
      >
        <Icon className={compact ? 'size-4' : 'size-5'} />
      </span>

      {compact ? (
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold leading-6 text-foreground">
            {section.title}
          </span>
          <Typography variant="supporting" className="mt-0.5 block">
            {section.description}
          </Typography>
        </span>
      ) : (
        <div className="min-w-0 flex-1 space-y-2">
          <Typography variant="cardTitle" className="min-w-0 flex-1">
            {section.title}
          </Typography>
          <Typography variant="supporting" className="block">
            {section.description}
          </Typography>
        </div>
      )}

      <ArrowRight
        className={
          compact
            ? 'size-5 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-interaction'
            : 'size-5 shrink-0 self-center text-muted-foreground transition group-hover:translate-x-1 group-hover:text-interaction'
        }
      />
    </Link>
  )
}

function ProductCatalogSectionsGrid({ sections, stacked = false }) {
  if (stacked) {
    return (
      <div className="space-y-2.5">
        {sections.map((section) => (
          <ProductCatalogSectionCard key={section.id} section={section} compact />
        ))}
      </div>
    )
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {sections.map((section) => (
        <ProductCatalogSectionCard key={section.id} section={section} />
      ))}
    </div>
  )
}

function ProductCatalogSectionPanel({
  sections,
  eyebrow = 'Catalog areas',
  title = 'Choose where you want to work',
  stacked = false,
}) {
  return (
    <section className="rounded-xl border border-border bg-elevated p-5">
      <div className="space-y-5">
        <div className="space-y-1">
          <Typography variant="eyebrow" className="block">
            {eyebrow}
          </Typography>
          <Typography variant="h3">{title}</Typography>
        </div>

        <ProductCatalogSectionsGrid sections={sections} stacked={stacked} />
      </div>
    </section>
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/products/')({
  component: ProductCatalogPage,
  validateSearch: (search) => productListSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedProductListSearchParams)],
  },
  loaderDeps: ({ search }) => ({ filters: getProductListFilters(search) }),
  loader: async ({ context, params, deps: { filters } }) => {
    const canListProducts = context.ability.can('list', 'products')
    const canViewCollections = context.ability.can('list', 'collections')
    const canViewPriceChanges = context.ability.can('edit', 'products')

    if (!hasProductCatalogAccess(context.ability)) throw notFound()

    if (canListProducts) {
      void prefetchIndexTableRouteQuery(
        context.queryClient,
        productQueries.index({ organizationId: params.orgId, filters })
      )
    }

    return {
      canListProducts,
      canViewCollections,
      canViewPriceChanges,
      isReadOnly: !canViewPriceChanges,
    }
  },
})

export function ProductCatalogPage() {
  const { orgId } = Route.useParams()
  const { canListProducts, canViewCollections, canViewPriceChanges } = Route.useLoaderData()
  const path = usePathHelper()
  const isGlobalOrg = useIsGlobalOrg()
  const search = Route.useSearch()
  const sections = buildProductCatalogSections({
    orgId,
    path,
    canListProducts,
    canViewCollections,
    canViewPriceChanges,
  })

  return (
    <div className="w-full max-w-none space-y-4">
      <ProductCatalogNav active="products" />
      <AdminPageHeader
        icon={Flower2}
        title="Product Catalog"
        description="Manage the products that appear in your storefront."
        actions={
          <>
            {canListProducts && !isGlobalOrg ? (
              <AdminHeaderActionButton variant="outline" asChild>
                <Link {...path.catalogBuilder.index().link}>
                  <Printer />
                  Print Catalog
                </Link>
              </AdminHeaderActionButton>
            ) : null}
            <Can do="create" on="products">
              <CreateActionButton asChild>
                <Link {...path.products.link('create')}>Create Product</Link>
              </CreateActionButton>
            </Can>
          </>
        }
      />

      {canListProducts ? (
        <ProductListTable orgId={orgId} search={search} />
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)] xl:items-start">
          <main className="space-y-5 xl:order-1">
            <ProductCatalogSectionPanel sections={sections} />
          </main>

          <aside aria-hidden="true" className="hidden xl:block xl:order-2" />
        </div>
      )}
    </div>
  )
}
